import type { ReactElement, ReactNode } from 'react';
import { useTranslate } from '@tolgee/react';
import { formatChf } from '@agentselly/ui';
import { AppLink } from '~/components/AppLink';

export function V3Page(props: { readonly children: ReactNode }): ReactElement {
  return (
    <div className="v3-overview">
      <div className="stack">{props.children}</div>
    </div>
  );
}

export function PageHead(props: {
  readonly title: string;
  readonly sub?: string;
  readonly end?: ReactNode;
  readonly dataWidget?: string;
  readonly 'data-widget'?: string;
  readonly odId?: string;
  readonly badges?: ReactNode;
}): ReactElement {
  return (
    <div
      className="hrow"
      data-widget={props['data-widget'] ?? props.dataWidget}
    >
      <div
        className="head"
        data-od-id={props.odId}
      >
        <h1>{props.title}</h1>
        {props.badges ? (
          <div
            className="row wrap"
            style={{ gap: 8, marginTop: 8 }}
            data-od-id="cockpit-head-badges"
          >
            {props.badges}
          </div>
        ) : null}
        {props.sub ? <p className="mu">{props.sub}</p> : null}
      </div>
      {props.end}
    </div>
  );
}

export function Kpi(props: {
  readonly value: ReactNode;
  readonly label: string;
  readonly delta?: number | null;
  readonly note?: ReactNode;
  readonly odId?: string;
  readonly current?: boolean;
  readonly dataWidget?: string;
  readonly 'data-widget'?: string;
}): ReactElement {
  const d = props.delta;
  return (
    <div
      className="kpi"
      data-widget={props['data-widget'] ?? props.dataWidget}
      data-od-id={props.odId}
    >
      <div className={`n num${props.current ? ' cur' : ''}`}>{props.value}</div>
      <div className="l">{props.label}</div>
      {d ? (
        <div className={`d ${d > 0 ? 'up' : 'down'}`}>
          {d > 0 ? '+' : '\u2212'}
          {Math.abs(d)} {'ggü. Vorwoche'}
        </div>
      ) : null}
      {props.note ? (
        <div
          className="xs mu"
          style={{ marginTop: 4 }}
        >
          {props.note}
        </div>
      ) : null}
    </div>
  );
}

export function Privacy(props: { readonly children: ReactNode }): ReactElement {
  return <p className="note xs mu">{props.children}</p>;
}

export function CardLink(props: {
  readonly to?: string;
  readonly odId?: string;
  readonly children: ReactNode;
}): ReactElement | null {
  if (!props.to) return null;
  return (
    <AppLink
      to={props.to}
      className="btn ghost"
      data-od-id={props.odId}
    >
      {props.children}
      <Ico
        name="arrow"
        size={16}
      />
    </AppLink>
  );
}

export function fmtChf(value?: number | string | null): string {
  const n = Number(value);
  return value == null || value === '' || !Number.isFinite(n) || n === 0
    ? '–'
    : formatChf(n, { maximumFractionDigits: 0 });
}

export function fmtDate(value?: string | number | Date | null): string {
  if (value == null || value === '') return '–';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '–'
    : d.toLocaleDateString('de-CH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
}

export function fmtTime(value?: string | number | Date | null): string {
  const d = new Date(value ?? NaN);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

export function Bars(props: {
  readonly rows: ReadonlyArray<{
    readonly label: string;
    readonly value: number;
    readonly text?: string;
  }>;
  readonly max?: number;
}): ReactElement {
  const max = props.max ?? Math.max(1, ...props.rows.map((r) => r.value));
  return (
    <div className="bars">
      {props.rows.map((r) => (
        <div
          className="bar"
          key={r.label}
        >
          <span className="lb">{r.label}</span>
          <span className="v num">{r.text ?? r.value}</span>
          <span
            className="trk"
            aria-hidden="true"
          >
            <span
              className="fil"
              style={{ width: `${Math.min(100, (r.value / max) * 100)}%` }}
            />
          </span>
        </div>
      ))}
    </div>
  );
}

export function LineChart(props: {
  readonly values: readonly number[];
  readonly labels: readonly [string, string];
  readonly title: string;
}): ReactElement {
  const w = 600;
  const h = 160;
  const max = Math.max(1, ...props.values);
  const step = props.values.length > 1 ? w / (props.values.length - 1) : w;
  const pts = props.values.map(
    (v, i) =>
      `${(i * step).toFixed(1)},${(h - (v / max) * (h - 8) - 4).toFixed(1)}`,
  );
  const line = pts.join(' ');
  return (
    <figure className="chart">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={props.title}
      >
        <line
          className="gl"
          x1="0"
          x2={w}
          y1={h - 1}
          y2={h - 1}
        />
        {pts.length > 1 ? (
          <>
            <polygon
              className="ar"
              points={`0,${h} ${line} ${w},${h}`}
            />
            <polyline
              className="ln"
              points={line}
            />
          </>
        ) : null}
      </svg>
      <div className="ax">
        <span>{props.labels[0]}</span>
        <span>{props.labels[1]}</span>
      </div>
    </figure>
  );
}

export function Steps(props: {
  readonly done: readonly boolean[];
  readonly label: string;
}): ReactElement {
  const reached = props.done.filter(Boolean).length;
  return (
    <div className="stp">
      <div
        className="stp-t"
        aria-hidden="true"
      >
        {props.done.map((on, i) => (
          <span
            key={i}
            style={{ display: 'contents' }}
          >
            {i > 0 ? <b className={on ? 'on' : ''} /> : null}
            <i
              className={`${on ? 'on' : ''} ${
                on && i === reached - 1 ? 'cur' : ''
              }`}
            />
          </span>
        ))}
      </div>
      <span className="stp-n">{props.label}</span>
    </div>
  );
}

const ICONS = {
  arrow: 'M5 12h14M13 6l6 6-6 6',
  doc: 'M6 2h9l5 5v15H6zM14 2v6h6',
  cal: 'M4 5h16v16H4zM4 10h16M9 3v4M15 3v4',
  mail: 'M3 5h18v14H3zM3 6l9 7 9-7',
  phone:
    'M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z',
  user: 'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21c0-4 4-6 8-6s8 2 8 6',
  users:
    'M9 11a4 4 0 100-8 4 4 0 000 8zM2 21c0-4 3-6 7-6s7 2 7 6M16 3a4 4 0 010 8M18 15c2 .5 4 2 4 6',
  folder: 'M3 6h6l2 2h10v12H3z',
  house: 'M3 11l9-8 9 8v10H3zM9 21v-7h6v7',
  coin: 'M12 21a9 9 0 100-18 9 9 0 000 18zM9 9h4.5a2 2 0 010 4H9h5a2 2 0 010 4H9M11 7v12',
  info: 'M12 21a9 9 0 100-18 9 9 0 000 18zM12 11v5M12 8v.5',
  chev: 'M6 9l6 6 6-6',
  check: 'M4 12l5 5L20 6',
  gauge: 'M4 18a9 9 0 1116 0M12 18l4-6',
  map: 'M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2zM9 4v14M15 6v14',
  chart: 'M4 20V4M4 20h16M8 16v-5M12 16V8M16 16v-3',
  retry: 'M4 4v6h6M20 20v-6h-6M5 15a8 8 0 0014 3M19 9A8 8 0 005 6',
} as const;

export type IconName = keyof typeof ICONS;

export function Ico(props: {
  readonly name: IconName;
  readonly size?: number;
}): ReactElement {
  const size = props.size ?? 18;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={ICONS[props.name]} />
    </svg>
  );
}

export function NewBadge(): ReactElement {
  const { t } = useTranslate();
  return (
    <span
      className="new"
      title={t(
        'cockpit_v3_new_hint',
        'Neuer Datenbedarf \u2013 liefert die unified-api heute nicht',
      )}
    >
      {t('cockpit_v3_new', 'NEU')}
    </span>
  );
}

export function initials(name?: string): string {
  return (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((x) => x[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function Avatar(props: {
  readonly name: string;
  readonly labelled?: boolean;
}): ReactElement {
  return (
    <span
      className="av2"
      {...(props.labelled
        ? { role: 'img', 'aria-label': props.name }
        : { 'aria-hidden': true })}
    >
      {initials(props.name)}
    </span>
  );
}

export function ContactActions(props: {
  readonly name: string;
  readonly phone?: string;
  readonly email?: string;
}): ReactElement | null {
  const { t } = useTranslate();
  if (!props.phone && !props.email) return null;
  return (
    <span className="ctc">
      {props.phone ? (
        <a
          href={`tel:${props.phone.replace(/\s/g, '')}`}
          title={props.phone}
          aria-label={`${props.name} ${t('cockpit_v3_call', 'anrufen')}: ${
            props.phone
          }`}
        >
          <Ico
            name="phone"
            size={16}
          />
        </a>
      ) : null}
      {props.email ? (
        <a
          href={`mailto:${props.email}`}
          title={props.email}
          aria-label={`${t('cockpit_v3_mail_to', 'E-Mail an')} ${props.name}: ${
            props.email
          }`}
        >
          <Ico
            name="mail"
            size={16}
          />
        </a>
      ) : null}
    </span>
  );
}

export function EmptyBlock(props: {
  readonly title: string;
  readonly text: string;
  readonly icon?: IconName;
  readonly actions?: ReactNode;
}): ReactElement {
  return (
    <div
      className="card stack state-empty"
      data-od-id="empty-block"
    >
      <span className="ico">
        <Ico
          name={props.icon ?? 'house'}
          size={24}
        />
      </span>
      <h2>{props.title}</h2>
      <p
        className="mu"
        style={{ maxWidth: '56ch' }}
      >
        {props.text}
      </p>
      {props.actions ? <div className="row wrap">{props.actions}</div> : null}
    </div>
  );
}

export function Accordion(props: {
  readonly label: string;
  readonly count: number;
  readonly odId?: string;
  readonly children: ReactNode;
}): ReactElement {
  return (
    <details
      className="acc"
      data-od-id={props.odId}
    >
      <summary>
        <span>
          {props.label} ({props.count})
        </span>
        <span className="chev">
          <Ico name="chev" />
        </span>
      </summary>
      <div className="in">{props.children}</div>
    </details>
  );
}

/** Funnel rows show the count plus the share of the first step. */
export function Funnel(props: {
  readonly steps: ReadonlyArray<{
    readonly label: string;
    readonly value: number;
  }>;
  readonly ariaLabel: string;
}): ReactElement {
  const first = props.steps[0]?.value ?? 0;
  return (
    <div
      className="bars"
      role="list"
      aria-label={props.ariaLabel}
    >
      {props.steps.map((s) => {
        const w = Math.max(
          s.value > 0 ? 2 : 0,
          first ? Math.round((s.value / first) * 100) : 0,
        );
        return (
          <div
            className="bar"
            role="listitem"
            key={s.label}
          >
            <span className="lb">{s.label}</span>
            <span className="v num">
              {s.value}{' '}
              <span
                className="mu"
                style={{ fontWeight: 400 }}
              >
                {'\u00b7'} {first ? Math.round((s.value / first) * 100) : 0} %
              </span>
            </span>
            <span
              className="trk"
              aria-hidden="true"
            >
              <i
                className="fil"
                style={{ width: `${w}%` }}
              />
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Offers over time with an optional dashed reference line (estimated value). */
export function TrendChart(props: {
  readonly points: ReadonlyArray<{
    readonly date: string;
    readonly value: number;
  }>;
  readonly reference?: number;
  readonly alt: string;
}): ReactElement | null {
  const pts = props.points;
  if (!pts.length) return null;
  const w = 360;
  const h = 170;
  const ref = props.reference && props.reference > 0 ? props.reference : 0;
  const vals = pts.map((p) => p.value).concat(ref ? [ref] : []);
  const mx = Math.max(...vals) * 1.03;
  const mn = Math.min(...vals) * 0.95;
  const span = mx - mn || 1;
  const x = (i: number): number =>
    pts.length > 1 ? 20 + (i * (w - 40)) / (pts.length - 1) : w / 2;
  const y = (v: number): number => h - 10 - ((v - mn) / span) * (h - 20);
  const d = pts
    .map(
      (p, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)} ${y(p.value).toFixed(1)}`,
    )
    .join(' ');
  return (
    <figure className="chart tr">
      <svg
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={props.alt}
      >
        <line
          className="gl"
          x1="0"
          x2={w}
          y1={h - 1}
          y2={h - 1}
        />
        <line
          className="gl"
          x1="0"
          x2={w}
          y1={h / 2}
          y2={h / 2}
        />
        {ref ? (
          <line
            className="ref"
            x1="0"
            x2={w}
            y1={y(ref).toFixed(1)}
            y2={y(ref).toFixed(1)}
          />
        ) : null}
        <path
          className="ln"
          d={d}
        />
        {pts.map((p, i) => (
          <circle
            key={`${p.date}-${i}`}
            className="pt"
            r="5"
            cx={x(i).toFixed(1)}
            cy={y(p.value).toFixed(1)}
          />
        ))}
      </svg>
      <figcaption className="ax">
        <span>{fmtDate(pts[0].date)}</span>
        <span>{fmtDate(pts[pts.length - 1].date)}</span>
      </figcaption>
    </figure>
  );
}

/** 5-step buyer progress (1 = downloaded ... 5 = offer given). */
export function Progress(props: {
  readonly step: number;
  readonly labels: readonly string[];
}): ReactElement {
  const { step, labels } = props;
  const p = Math.min(labels.length, Math.max(1, step));
  return (
    <span
      className="stp"
      role="progressbar"
      aria-valuemin={1}
      aria-valuemax={labels.length}
      aria-valuenow={p}
      aria-valuetext={`Schritt ${p} von ${labels.length}: ${labels[p - 1]}`}
    >
      <span
        className="stp-t"
        aria-hidden="true"
      >
        {labels.map((l, i) => (
          <span
            key={l}
            style={{ display: 'contents' }}
          >
            {i ? <b className={i < p ? 'on' : ''} /> : null}
            <i className={`${i < p ? 'on' : ''}${i === p - 1 ? ' cur' : ''}`} />
          </span>
        ))}
      </span>
      <span className="stp-n">{labels[p - 1]}</span>
    </span>
  );
}
